import React, {FunctionComponent, ReactElement, useState} from "react";
import AnimalSearchResult from "../../api/dtos/animal-search-result";
import FlexStack from "./flex-stack";
import {SearchField} from "./index";
import {Stack, SxProps, Theme} from "@mui/material";
import AnimalSearchResultCard from "./animal-search-result-card";
import {SearchService} from "../../services/search-service";
import {AnimalService} from "../../services/animal-service";
import {useSuspenseQuery} from "@tanstack/react-query";
import {LoadingFallback} from "./loading-fallback";
import {ErrorBox} from "./alerts";
import {LoadingSuspense} from "./loading-suspense";

const MIN_SEARCH_TERM_LENGTH = 3;

type AnimalSearchProps = {
    onBeforeShowSuggestions?: (searchResults: AnimalSearchResult[]) => AnimalSearchResult[];
    handleResultClick: (clickedResult: AnimalSearchResult) => void;
    renderEmptyResult?: (name: string) => ReactElement;
    sx?: SxProps<Theme>;
    label?: string;
    searchRegistrations?: boolean;
    searchHerdAndPedigree?: boolean;
};

const SearchResultsRenderer = (props: {
    animalSearchResults: AnimalSearchResult[],
    currentSearchTerm: string,
    renderEmptyResult?: (name: string) => ReactElement,
    onResultClick: (a: AnimalSearchResult) => void
}) => {
    const {animalSearchResults, renderEmptyResult, onResultClick, currentSearchTerm} = props;
    if (currentSearchTerm !== null && currentSearchTerm.length < MIN_SEARCH_TERM_LENGTH) {
        return null;
    } else if (animalSearchResults.length === 0) {
        return renderEmptyResult?.(currentSearchTerm) || null;
    } else {
        return (
            <>
                {animalSearchResults.map(a => (
                    <AnimalSearchResultCard key={a.panonId.id}
                                            animalSearchResult={a}
                                            handleClick={() => onResultClick(a)}
                                            clickable/>
                ))}
            </>
        );
    }
};

/**
 * Combine Results for registered animals with herd and pedigree animals. If a search result for a registered animal exists, the pedigree and herd result is dropped for that
 * panon id.
 */
const combineResults = (registrations: AnimalSearchResult[], herdAndPedigree: AnimalSearchResult[]): AnimalSearchResult[] => {
    const registrationPanonIds = new Set(registrations.map(sr => sr.panonId.id));
    const filteredHerdAndPedigree = herdAndPedigree.filter(r => !registrationPanonIds.has(r.panonId.id));
    return [...registrations, ...filteredHerdAndPedigree];
};

const AnimalSearchResults = ({
                                 searchTerm,
                                 onBeforeShowSuggestions,
                                 handleResultClick,
                                 renderEmptyResult,
                                 searchRegistrations = true,
                                 searchHerdAndPedigree = true
                             }: Omit<AnimalSearchProps, 'sx' | 'label'> & { searchTerm: string }) => {
    const {data: foundAnimals} = useSuspenseQuery({
        queryKey: ['animal-search', {searchTerm}],
        queryFn: async () => {
            if (!searchTerm?.length || searchTerm.length < MIN_SEARCH_TERM_LENGTH) {
                return [];
            }
            const [registered, herdAndPedigree] = await Promise.all([
                searchRegistrations ? SearchService.searchRegisteredAnimals(searchTerm) : Promise.resolve([]),
                searchHerdAndPedigree ? SearchService.searchHerdAndPedigreeAnimals(searchTerm) : Promise.resolve([]),
            ]);
            const combinedResults = combineResults(registered, herdAndPedigree);
            return onBeforeShowSuggestions ? onBeforeShowSuggestions(combinedResults) : combinedResults;
        },
    });

    return (
        <Stack spacing={1}>
            <SearchResultsRenderer animalSearchResults={foundAnimals}
                                   currentSearchTerm={searchTerm}
                                   renderEmptyResult={renderEmptyResult}
                                   onResultClick={handleResultClick}/>
        </Stack>
    );
};

const AnimalSearch: FunctionComponent<AnimalSearchProps> = ({
                                                                onBeforeShowSuggestions,
                                                                handleResultClick,
                                                                renderEmptyResult,
                                                                sx,
                                                                label,
                                                                searchRegistrations = true,
                                                                searchHerdAndPedigree = true
                                                            }) => {

    const [animal, setAnimal] = useState<AnimalSearchResult | null>(null);
    const [currentSearchTerm, setCurrentSearchTerm] = useState('');

    const performSearch = async (searchTerm: string): Promise<AnimalSearchResult[]> => {
        setCurrentSearchTerm(searchTerm);
        return [];
    };

    return (
        <FlexStack sx={sx}>
            <SearchField<AnimalSearchResult> label={label ?? "Tier suchen"}
                                             showSearchIcon
                                             findSuggestions={performSearch}
                                             renderOption={a => AnimalService.getAnimalFullName(a)}
                                             optionUniqueKey={a => a.panonId.id}
                                             value={animal}
                                             onValueChange={setAnimal}/>
            <LoadingSuspense
                fallback={<LoadingFallback message={`Laden der Suchergebnisse für ${currentSearchTerm}...`}/>}
                error={<ErrorBox>Fehler bei Tiersuche</ErrorBox>}>
                <AnimalSearchResults searchTerm={currentSearchTerm}
                                     renderEmptyResult={renderEmptyResult}
                                     handleResultClick={handleResultClick}
                                     onBeforeShowSuggestions={onBeforeShowSuggestions}
                                     searchRegistrations={searchRegistrations}
                                     searchHerdAndPedigree={searchHerdAndPedigree}
                />
            </LoadingSuspense>
        </FlexStack>
    );
};

export default AnimalSearch